import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Row, Col } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import {
  SectionCard,
  SectionHeader,
  SectionTitle,
  ViewAllButton,
  StyledCollapse,
  TableContainer,
} from './styles';
import { DASHBOARD_ROUTES, BUTTON_LABELS } from './constants';
import { StaffSectionProps } from './types';
import { useTheme } from '../../../context/ThemeContext';

const { Panel } = StyledCollapse;

const StaffSection: React.FC<StaffSectionProps> = ({
  isOpen,
  isLoading = false,
  staffData = [],
  activeStaff,
  inactiveStaff,
  recentHires,
}) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const handleViewAll = () => {
    if (!isLoading) {
      navigate(DASHBOARD_ROUTES.STAFF);
    }
  };

  // Get top 5 staff members
  const topStaff = staffData.slice(0, 5);

  const columns: ColumnsType<any> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (text) => <span>{text || '-'}</span>,
    },
  ];

  return (
    <StyledCollapse
      activeKey={isOpen ? ['staff'] : []}
      ghost
      expandIcon={() => null}
    >
      <Panel key="staff" header={null}>
        <SectionCard>
          <SectionHeader>
            <SectionTitle level={4}>Staff Members</SectionTitle>
          </SectionHeader>

          {/* Stats row from managerReports.staffManagement */}
          {(activeStaff !== undefined || inactiveStaff !== undefined) && (
            <Row gutter={16} style={{ marginBottom: 20 }}>
              <Col span={8}>
                <div style={{
                  textAlign: 'center',
                  padding: '12px',
                  borderRadius: 8,
                  background: isDarkMode ? '#04220220' : '#F0FFF4',
                }}>
                  <CheckCircleOutlined style={{ color: '#48BB78', fontSize: 20, marginBottom: 4, display: 'block' }} />
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#48BB78' }}>{activeStaff ?? '—'}</div>
                  <div style={{ fontSize: 12, color: isDarkMode ? '#a0aec0' : '#718096' }}>Active</div>
                </div>
              </Col>
              <Col span={8}>
                <div style={{
                  textAlign: 'center',
                  padding: '12px',
                  borderRadius: 8,
                  background: isDarkMode ? '#5f222220' : '#FFF5F5',
                }}>
                  <CloseCircleOutlined style={{ color: '#F56565', fontSize: 20, marginBottom: 4, display: 'block' }} />
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#F56565' }}>{inactiveStaff ?? '—'}</div>
                  <div style={{ fontSize: 12, color: isDarkMode ? '#a0aec0' : '#718096' }}>Inactive</div>
                </div>
              </Col>
              <Col span={8}>
                <div style={{
                  textAlign: 'center',
                  padding: '12px',
                  borderRadius: 8,
                  background: isDarkMode ? '#1f3a6220' : '#EBF8FF',
                }}>
                  <UserAddOutlined style={{ color: '#4299E1', fontSize: 20, marginBottom: 4, display: 'block' }} />
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#4299E1' }}>{recentHires ?? '—'}</div>
                  <div style={{ fontSize: 12, color: isDarkMode ? '#a0aec0' : '#718096' }}>Recent Hires</div>
                </div>
              </Col>
            </Row>
          )}

          {topStaff.length > 0 ? (
            <TableContainer>
              <Table
                columns={columns}
                dataSource={topStaff.map((staff) => ({ ...staff, key: staff.id }))}
                pagination={false}
                loading={isLoading}
                size="small"
                bordered={false}
              />
            </TableContainer>
          ) : (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <p>No staff members found</p>
            </div>
          )}

          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
            <ViewAllButton type="primary" onClick={handleViewAll} disabled={isLoading}>
              {BUTTON_LABELS.VIEW_ALL_STAFF}
            </ViewAllButton>
          </div>
        </SectionCard>
      </Panel>
    </StyledCollapse>
  );
};

export default StaffSection;
